import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  TrendingUp,
  Users,
  AlertCircle,
  Smile,
  Eye
} from 'lucide-react';
import { ListeningStats as Stats } from '../../services/listeningService';

interface ListeningStatsProps {
  stats: Stats | null;
  isLoading: boolean;
}

const ListeningStats: React.FC<ListeningStatsProps> = ({ stats, isLoading }) => {
  const { t } = useTranslation();

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getSentimentColor = (score: number) => {
    if (score >= 0.6) return 'text-green-600 dark:text-green-400';
    if (score <= 0.4) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getSentimentLabel = (score: number) => {
    if (score >= 0.6) return t('listening.stats.positive', 'Positive');
    if (score <= 0.4) return t('listening.stats.negative', 'Negative');
    return t('listening.stats.neutral', 'Neutral');
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse"
          >
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-lg mb-2" />
            <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
            <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const statCards = [
    {
      label: t('listening.stats.totalMentions', 'Total Mentions'),
      value: formatNumber(stats.totalMentions),
      icon: MessageSquare,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      label: t('listening.stats.avgSentiment', 'Average Sentiment'),
      value: getSentimentLabel(parseFloat(stats.averageSentiment)),
      icon: Smile,
      iconBg: 'bg-green-100 dark:bg-green-900/30',
      iconColor: getSentimentColor(parseFloat(stats.averageSentiment))
    },
    {
      label: t('listening.stats.totalReach', 'Total Reach'),
      value: formatNumber(stats.totalReach),
      icon: Eye,
      iconBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400'
    },
    {
      label: t('listening.stats.influencers', 'Influencers'),
      value: formatNumber(stats.influencerMentions),
      icon: Users,
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      label: t('listening.stats.positive', 'Positive'),
      value: formatNumber(stats.sentimentBreakdown.positive),
      icon: TrendingUp,
      iconBg: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400'
    },
    {
      label: t('listening.stats.pending', 'Pending'),
      value: formatNumber(stats.pendingResponses),
      icon: AlertCircle,
      iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
      iconColor: 'text-yellow-600 dark:text-yellow-400'
    }
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
          >
            <div className={`w-8 h-8 rounded-lg ${stat.iconBg} flex items-center justify-center mb-2`}>
              <Icon className={`w-4 h-4 ${stat.iconColor}`} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {stat.label}
            </p>
            <p className={`text-lg font-bold ${stat.iconColor}`}>
              {stat.value}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default ListeningStats;
